import React from "react";
import { Link } from "react-router-dom";
import Seo from "./Seo";
import ProjectCard from "./ProjectCard";
import projects from "./projectsData";
import articles from "./articlesData";

/**
 * Home page with professional engineering focus.
 * Structure: Hero > Featured projects > Capabilities > Recent notes.
 */
export default function Home() {
  const featuredProjects = projects.filter((p) => p.featured);
  const recentNotes = articles.slice(0, 4);

  return (
    <div className="home-container">
      <Seo
        title="LinuxSecOps - Security Engineering Platform"
        description="Personal engineering platform focused on Linux infrastructure security, DevSecOps automation, and practical security tools developed through real projects."
        path="/"
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">Security Engineering</div>
          <h1 className="hero-title">
            Building Secure Infrastructure
            <span className="hero-title-accent"> Through Real Projects</span>
          </h1>
          <p className="hero-description">
            Personal engineering platform focused on Linux infrastructure security,
            DevSecOps automation, and practical security tools. Emphasis on
            auditability, minimal complexity, and measurable outcomes.
          </p>
          <div className="hero-actions">
            <a
              href="https://pentestscan.app"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Launch Scanner
            </a>
            <Link to="/about" className="btn-secondary">
              Learn More
            </Link>
          </div>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-value">3</span>
              <span className="stat-label">Active Projects</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-value">35+</span>
              <span className="stat-label">Security Scanners</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-value">{articles.length}</span>
              <span className="stat-label">Technical Notes</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">Featured Projects</h2>
          <p className="section-subtitle">
            Production-ready tools built to solve real security challenges
          </p>
        </div>
        <div className="projects-grid">
          {featuredProjects.map((project) => (
            <ProjectCard key={project.id} project={project} expanded={false} />
          ))}
        </div>
      </section>

      {/* Capabilities */}
      <section className="section capabilities-section">
        <div className="section-header">
          <h2 className="section-title">Capabilities</h2>
          <p className="section-subtitle">
            Technical expertise across security engineering domains
          </p>
        </div>
        <div className="capabilities-grid">
          <div className="capability-card">
            <div className="capability-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <h3 className="capability-title">DevSecOps Enablement</h3>
            <ul className="capability-list">
              <li>CI/CD hardening and secrets management</li>
              <li>Policy-as-code, compliance gates</li>
              <li>Artifact signing and provenance</li>
            </ul>
          </div>
          <div className="capability-card">
            <div className="capability-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <h3 className="capability-title">Security Engineering</h3>
            <ul className="capability-list">
              <li>Application security (SAST/DAST/SCA)</li>
              <li>Cloud posture (AWS/Azure/GCP)</li>
              <li>Detection engineering and SIEM</li>
            </ul>
          </div>
          <div className="capability-card">
            <div className="capability-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
            </div>
            <h3 className="capability-title">Infrastructure</h3>
            <ul className="capability-list">
              <li>Linux hardening and configuration</li>
              <li>Container and Kubernetes security</li>
              <li>Network segmentation, zero trust</li>
            </ul>
          </div>
          <div className="capability-card">
            <div className="capability-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 18 22 12 16 6"/>
                <polyline points="8 6 2 12 8 18"/>
              </svg>
            </div>
            <h3 className="capability-title">Development</h3>
            <ul className="capability-list">
              <li>React/Next.js, React Native</li>
              <li>Python backend (FastAPI/Django)</li>
              <li>PostgreSQL, Redis, cloud infra</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Recent Notes */}
      <section className="section">
        <div className="section-header">
          <div className="section-header-row">
            <div>
              <h2 className="section-title">Technical Notes</h2>
              <p className="section-subtitle">
                Guides, tutorials, and technical documentation
              </p>
            </div>
            <Link to="/learn" className="view-all-link">
              View all notes
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </Link>
          </div>
        </div>
        <div className="notes-grid">
          {recentNotes.map((article) => (
            <Link
              key={article.id}
              to={article.path}
              className="note-card"
            >
              <span className="note-category">{article.category}</span>
              <h3 className="note-title">{article.title}</h3>
              <span className="note-arrow">Read more →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Ready to discuss your security needs?</h2>
          <p className="cta-description">
            For consulting inquiries, technical discussions, or collaboration opportunities.
          </p>
          <a
            href="mailto:linuxsecops@proton.me"
            className="btn-primary"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
}
